import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { AlertCircle, CheckCircle2, Clock, PlayCircle, FileText, Award, BookOpen, ChevronRight, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrainingPortal({ token, user }) {
  const [activeTab, setActiveTab] = useState('my-trainings');
  const [assignments, setAssignments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Training view state
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [trainingStep, setTrainingStep] = useState('video'); // video, assessment, result
  
  // Assessment state
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-assignments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAssignments(data.assignments || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  }, [token]);

  const fetchCertificates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCertificates(data.certificates || []);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAssignments(), fetchCertificates()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAssignments, fetchCertificates]);

  const openTraining = (assignment) => {
    setSelectedAssignment(assignment);
    setTrainingStep(assignment.video_completed ? 'assessment' : 'video');
    setAnswers([]);
    setAssessmentResult(null);
    setShowTrainingDialog(true);
  };

  const getDaysRemaining = (issueDate) => {
    const issued = new Date(issueDate);
    const now = new Date();
    const diffDays = Math.ceil((now - issued) / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - diffDays);
  };

  const downloadCertificate = async (certId) => {
    try {
      const res = await fetch(`${API_URL}/api/certificates/${certId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Download failed');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('content-disposition') || '';
      const filename = disposition.match(/filename="(.+)"/)?.[1] || `Certificate_${certId}.pdf`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Download failed. Please try again.');
    }
  };

  const markVideoComplete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-assignments/${selectedAssignment.id}/mark-video-complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedAssignment({ ...selectedAssignment, video_completed: true });
        setTrainingStep('assessment');
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error marking video complete:', error);
    }
  };

  const startAssessment = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-assignments/${selectedAssignment.id}/start-assessment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setAssessment(data.assessment);
        // Initialize answers as arrays (for checkbox multi-select)
        setAnswers(new Array(data.assessment.questions.length).fill(null).map(() => []));
      }
    } catch (error) {
      console.error('Error starting assessment:', error);
    }
  };

  const submitAssessment = async () => {
    // Check all questions have at least one answer
    const unanswered = answers.some(a => !a || a.length === 0);
    if (unanswered) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/training-assignments/${selectedAssignment.id}/submit-assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          assignment_id: selectedAssignment.id,
          answers  // Each answer is an array of selected option indices
        })
      });
      const data = await res.json();
      if (res.ok) {
        setAssessmentResult(data);
        setTrainingStep('result');
        fetchAssignments();
        if (data.passed) {
          fetchCertificates();
        }
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (assignment) => {
    const isOverdue = new Date(assignment.deadline) < new Date() && assignment.status !== 'completed';
    
    if (assignment.status === 'completed') {
      return <Badge className="bg-green-500" data-testid="status-completed">Completed</Badge>;
    }
    if (assignment.status === 'failed') {
      return <Badge variant="destructive" data-testid="status-failed">Failed</Badge>;
    }
    if (isOverdue) {
      return <Badge variant="destructive" data-testid="status-overdue">Overdue</Badge>;
    }
    if (assignment.status === 'in_progress') {
      return <Badge className="bg-blue-500" data-testid="status-in-progress">In Progress</Badge>;
    }
    return <Badge variant="secondary" data-testid="status-pending">Pending</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const pendingTrainings = assignments.filter(a => ['pending', 'in_progress', 'failed'].includes(a.status));
  const completedTrainings = assignments.filter(a => a.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="training-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="training-portal">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="summary-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Assigned</p>
                <p className="text-2xl font-bold">{assignments.length}</p>
              </div>
              <BookOpen className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-pending">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{pendingTrainings.length}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-completed">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{completedTrainings.length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-certificates">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Certificates</p>
                <p className="text-2xl font-bold text-purple-600">{certificates.length}</p>
              </div>
              <Award className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2" data-testid="training-tabs">
          <TabsTrigger value="my-trainings" data-testid="tab-my-trainings">My Trainings</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-certificates">Certificates</TabsTrigger>
        </TabsList>

        <TabsContent value="my-trainings" className="space-y-4">
          {assignments.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No trainings assigned yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {assignments.map((assignment) => {
                const isOverdue = new Date(assignment.deadline) < new Date() && assignment.status !== 'completed';
                return (
                  <Card 
                    key={assignment.id} 
                    className={`${isOverdue ? 'border-red-300 bg-red-50' : ''}`}
                    data-testid={`training-card-${assignment.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{assignment.training_title}</CardTitle>
                          <CardDescription>
                            Assigned on {formatDate(assignment.assigned_at)}
                          </CardDescription>
                        </div>
                        {getStatusBadge(assignment)}
                      </div>
                    </CardHeader>
                    <CardContent className="pb-2">
                      <div className="flex items-center gap-6 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>Deadline: {formatDate(assignment.deadline)}</span>
                        </div>
                        {assignment.score !== null && (
                          <div className="flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            <span>Score: {assignment.score?.toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                      {/* Progress indicator */}
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`h-2 w-2 rounded-full ${assignment.video_completed ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className="text-sm">Video</span>
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                          <div className={`h-2 w-2 rounded-full ${assignment.assessment_completed_at ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className="text-sm">Assessment</span>
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                          <div className={`h-2 w-2 rounded-full ${assignment.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className="text-sm">Certificate</span>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter>
                      {assignment.status === 'completed' ? (
                        <Button variant="outline" disabled data-testid="btn-completed">
                          <CheckCircle2 className="h-4 w-4 mr-2" /> Completed
                        </Button>
                      ) : (
                        <Button onClick={() => openTraining(assignment)} data-testid="btn-start-training">
                          <PlayCircle className="h-4 w-4 mr-2" />
                          {assignment.video_completed ? 'Take Assessment' : 'Start Training'}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="certificates" className="space-y-4">
          {certificates.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No certificates earned yet. Complete trainings to earn certificates!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {certificates.map((cert) => {
                const daysLeft = getDaysRemaining(cert.issue_date);
                const canDownload = daysLeft > 0;
                return (
                  <Card key={cert.id} className="overflow-hidden shadow-lg border-0" data-testid={`certificate-${cert.id}`}>
                    {/* Header: Grey with golden accent */}
                    <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src="/gbt-logo.png" alt="GBT" className="h-8 w-auto rounded" />
                        <span className="text-amber-400 font-semibold text-sm tracking-wider">GREY BULL TECHNOLOGIES LLP</span>
                      </div>
                      <Award className="h-7 w-7 text-amber-400" />
                    </div>
                    <div className="h-1 bg-gradient-to-r from-amber-700 via-amber-400 to-amber-700"></div>
                    
                    {/* Certificate Body */}
                    <CardContent className="pt-5 pb-4 bg-gradient-to-b from-gray-50 to-white">
                      <div className="text-center mb-4">
                        <h3 className="text-gray-700 text-xs tracking-[4px] uppercase mb-1">Certificate of Completion</h3>
                        <div className="w-16 h-px bg-amber-500 mx-auto"></div>
                      </div>
                      
                      <div className="text-center space-y-2">
                        <p className="text-xs text-gray-500">This certifies that</p>
                        <p className="text-xl font-bold text-gray-800 italic">{cert.employee_name}</p>
                        <p className="text-xs text-gray-500">has successfully completed</p>
                        <p className="text-base font-semibold text-amber-700">&ldquo;{cert.training_title}&rdquo;</p>
                      </div>

                      <div className="text-center my-4">
                        <span className="inline-block bg-gradient-to-r from-amber-700 to-amber-500 text-white px-5 py-1.5 rounded-full text-sm font-bold tracking-wide">
                          Score: {cert.score?.toFixed(1)}%
                        </span>
                      </div>

                      <div className="flex justify-between items-end px-2 mt-4 pt-3 border-t border-gray-200">
                        <div>
                          <p className="text-xs text-gray-400">Issued</p>
                          <p className="text-sm text-gray-700 font-medium">{formatDate(cert.issue_date)}</p>
                          <p className="text-[10px] text-gray-400 mt-1">ID: {cert.id.slice(0, 8)}...</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-700 font-bold">Shipra Tyagi</p>
                          <p className="text-[10px] text-gray-500">Managing Director</p>
                        </div>
                      </div>
                    </CardContent>

                    {/* Download Footer */}
                    <div className="bg-gray-100 px-5 py-3 flex items-center justify-between border-t">
                      {canDownload ? (
                        <>
                          <span className="text-xs text-gray-500">
                            Download available for {daysLeft} more day{daysLeft !== 1 ? 's' : ''}
                          </span>
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={() => downloadCertificate(cert.id)}
                            data-testid={`download-cert-${cert.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" /> Download PDF
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-red-500 w-full text-center">
                          Download period expired (30 days from issue)
                        </span>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Training Dialog */}
      <Dialog open={showTrainingDialog} onOpenChange={setShowTrainingDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="training-dialog">
          {selectedAssignment && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedAssignment.training_title}</DialogTitle>
                <DialogDescription>
                  {trainingStep === 'video' && 'Watch the video to proceed to the assessment'}
                  {trainingStep === 'assessment' && (assessment ? 'Answer all questions' : 'Click Start to begin the assessment')}
                  {trainingStep === 'result' && 'Assessment Results'}
                </DialogDescription>
              </DialogHeader>

              {/* Video Step */}
              {trainingStep === 'video' && (
                <div className="space-y-4">
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    {selectedAssignment.video_file_id ? (
                      <video
                        src={`${API_URL}/api/trainings/video/${selectedAssignment.video_file_id}`}
                        controls
                        className="w-full h-full"
                        data-testid="training-video-player"
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <iframe
                        src={selectedAssignment.video_url || "https://www.youtube.com/embed/dQw4w9WgXcQ"}
                        title="Training Video"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    )}
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Training Content:</h4>
                    <p className="text-gray-600">{selectedAssignment.text_content || 'Watch the video above to learn the key concepts.'}</p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowTrainingDialog(false)}>
                      Close
                    </Button>
                    <Button onClick={markVideoComplete} data-testid="btn-mark-video-complete">
                      Mark as Watched & Continue <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {/* Assessment Step */}
              {trainingStep === 'assessment' && !assessment && (
                <div className="space-y-4 text-center py-8">
                  <FileText className="h-16 w-16 mx-auto text-blue-500" />
                  <h3 className="text-xl font-semibold">Ready for Assessment?</h3>
                  <p className="text-gray-600">
                    You need to score at least 80% to pass and receive your certificate.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800">Important:</p>
                        <ul className="text-sm text-yellow-700 list-disc list-inside">
                          <li>Read each question carefully</li>
                          <li>You must answer all questions</li>
                          <li>Minimum passing score: 80%</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowTrainingDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={startAssessment} data-testid="btn-start-assessment">
                      Start Assessment
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {trainingStep === 'assessment' && assessment && (
                <div className="space-y-6">
                  <Progress value={(answers.filter(a => a && a.length > 0).length / assessment.questions.length) * 100} />
                  <p className="text-sm text-gray-500 text-center">
                    Answered {answers.filter(a => a && a.length > 0).length} of {assessment.questions.length} questions
                  </p>
                  
                  {assessment.questions.map((q, qIndex) => (
                    <Card key={q.id} className={answers[qIndex]?.length > 0 ? 'border-green-200' : ''}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">
                          Q{qIndex + 1}. {q.question}
                        </CardTitle>
                        <p className="text-xs text-gray-500">Select all correct answers ({q.points} points)</p>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {q.options.map((option, oIndex) => (
                            <div key={oIndex} className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50">
                              <Checkbox
                                id={`q${qIndex}-o${oIndex}`}
                                checked={answers[qIndex]?.includes(oIndex) || false}
                                onCheckedChange={(checked) => {
                                  const newAnswers = [...answers];
                                  const current = [...(newAnswers[qIndex] || [])];
                                  if (checked) {
                                    current.push(oIndex);
                                  } else {
                                    const idx = current.indexOf(oIndex);
                                    if (idx > -1) current.splice(idx, 1);
                                  }
                                  newAnswers[qIndex] = current;
                                  setAnswers(newAnswers);
                                }}
                                data-testid={`answer-${qIndex}-${oIndex}`}
                              />
                              <Label htmlFor={`q${qIndex}-o${oIndex}`} className="cursor-pointer flex-1">
                                {option}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowTrainingDialog(false)}>
                      Save & Exit
                    </Button>
                    <Button 
                      onClick={submitAssessment} 
                      disabled={submitting || answers.some(a => !a || a.length === 0)}
                      data-testid="btn-submit-assessment"
                    >
                      {submitting ? 'Submitting...' : 'Submit Assessment'}
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {/* Result Step */}
              {trainingStep === 'result' && assessmentResult && (
                <div className="space-y-6 text-center py-4">
                  {assessmentResult.passed ? (
                    <>
                      <div className="bg-green-100 rounded-full p-6 w-24 h-24 mx-auto flex items-center justify-center">
                        <CheckCircle2 className="h-12 w-12 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-green-600">Congratulations!</h3>
                      <p className="text-gray-600">You have passed the assessment!</p>
                    </>
                  ) : (
                    <>
                      <div className="bg-red-100 rounded-full p-6 w-24 h-24 mx-auto flex items-center justify-center">
                        <AlertCircle className="h-12 w-12 text-red-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-red-600">Not Passed</h3>
                      <p className="text-gray-600">You can try again.</p>
                    </>
                  )}
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-sm text-gray-500">Your Score</p>
                        <p className={`text-2xl font-bold ${assessmentResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                          {assessmentResult.score?.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Passing Score</p>
                        <p className="text-2xl font-bold text-gray-600">{assessmentResult.passing_score}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Correct Answers</p>
                        <p className="text-2xl font-bold text-gray-600">
                          {assessmentResult.correct_answers}/{assessmentResult.total_questions}
                        </p>
                      </div>
                    </div>
                  </div>

                  {assessmentResult.passed && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <Award className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-green-700 font-medium">
                        Your certificate has been generated and emailed to you!
                      </p>
                    </div>
                  )}

                  <DialogFooter className="justify-center">
                    <Button onClick={() => setShowTrainingDialog(false)}>
                      Close
                    </Button>
                    {!assessmentResult.passed && (
                      <Button variant="outline" onClick={() => {
                        setTrainingStep('assessment');
                        setAssessment(null);
                        setAnswers([]);
                        setAssessmentResult(null);
                      }}>
                        Try Again
                      </Button>
                    )}
                  </DialogFooter>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
